import { useEffect, useState } from 'react'
import { useSnackbar } from 'notistack'

export type ToastKey = string

export interface ToastStackState {
  toastKeys: ToastKey[]
  exitingToastKeys: ToastKey[]
}

export type SubscribeToToastStack = (
  listener: (toastStack: ToastStackState) => void
) => () => void

interface UseToastControllerProps {
  snackBarKey: ToastKey
  type: string
  subscribeToToastStack?: SubscribeToToastStack
  removeToast?: (key: ToastKey) => void
}

const useToastController = ({ snackBarKey, type, subscribeToToastStack, removeToast }: UseToastControllerProps) => {
  const [stackPosition, setStackPosition] = useState(0)
  const [isExiting, setIsExiting] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const { closeSnackbar } = useSnackbar()

  useEffect(() => {
    return () => {
      removeToast?.(snackBarKey)
    }
  }, [snackBarKey, removeToast])

  useEffect(() => {
    if (!subscribeToToastStack) return undefined

    return subscribeToToastStack(({ toastKeys, exitingToastKeys }) => {
      const toastIndex = toastKeys.indexOf(snackBarKey)

      if (toastIndex !== -1) {
        setStackPosition(toastKeys.length - 1 - toastIndex)
      }

      setIsExiting(exitingToastKeys.includes(snackBarKey))
    })
  }, [snackBarKey, subscribeToToastStack])

  const closeToast = () => {
    closeSnackbar(snackBarKey)
  }

  const onMouseEnter = () => {
    setIsHovered(true)
  }

  const onMouseLeave = () => {
    setIsHovered(false)
  }

  return { stackPosition, isExiting, isProgressPaused: isHovered || (type === 'error' && stackPosition > 0), closeToast, onMouseEnter, onMouseLeave }
}

export default useToastController
